/**
 * Raw Compass Data Service for FlowRide
 * Provides device orientation using sensor fusion (compass + gyroscope) or AbsoluteOrientationSensor
 * Emits raw, uncalibrated compass data
 */

import { SensorFusion, type SensorData } from "../../libs/SensorFusion";
import { CompassData, NavigationEventType, type NavigationEvent } from "../../types/Navigation";
import { compassPermissionService } from "./CompassPermissionService";

/**
 * Event listener callback type
 */
type CompassEventListener = ( data: CompassData ) => void;

/**
 * Sensor mode
 */
export enum SensorMode {

    /** Use sensor fusion (magnetometer + gyroscope + accelerometer) */
    "SENSOR_FUSION" = "sensor_fusion",

    /** Use AbsoluteOrientationSensor API */
    "ABSOLUTE_ORIENTATION" = "absolute_orientation",

    /** Use basic DeviceOrientationEvent */
    "BASIC" = "basic",
}

/**
 * Service that provides raw compass data from device sensors
 */
export class RawCompassDataService {

    /** Singleton instance */
    private static instance: RawCompassDataService | null = null;

    /** Last compass data */
    private lastValue: CompassData = {
        "heading":    0,
        "calibrated": false,
        "offset":     0,
        "timestamp":  0,
    };

    /** Event listeners */
    private listeners: Set<CompassEventListener> = new Set();

    /** Current sensor mode */
    private sensorMode: SensorMode = SensorMode.BASIC;

    /** Sensor fusion instance */
    private sensorFusion: SensorFusion | null = null;

    /** AbsoluteOrientationSensor instance */
    private absoluteOrientationSensor: AbsoluteOrientationSensor | null = null;

    /** Whether the service is active */
    private active: boolean = false;

    /** Calibration offset (applied externally by calibration service) */
    private calibrationOffset: number = 0;

    /** Whether compass is calibrated */
    private calibrated: boolean = false;

    /** Last raw heading (before calibration) */
    private lastRawHeading: number | null = null;

    /** Update throttle interval in ms */
    private readonly UPDATE_INTERVAL = 33; // ~30 fps

    /** Last update timestamp */
    private lastUpdateTime: number = 0;

    /**
     * Private constructor for singleton pattern
     */
    private constructor() {
    // Detect best available sensor mode
        this.detectBestSensorMode();
    }

    /**
     * Get singleton instance
     * @returns Service instance
     */
    public static getInstance(): RawCompassDataService {
        if ( RawCompassDataService.instance === null ) {
            RawCompassDataService.instance = new RawCompassDataService();
        }

        return RawCompassDataService.instance;
    }

    /**
     * Get the last compass data
     * @returns Last compass data or null if no data available
     */
    public getLastValue(): CompassData {
        return this.lastValue;
    }

    /**
     * Detect best available sensor mode
     */
    private detectBestSensorMode(): void {
        // Check for AbsoluteOrientationSensor
        if ( typeof window !== "undefined" && "AbsoluteOrientationSensor" in window ) {
            this.sensorMode = SensorMode.ABSOLUTE_ORIENTATION;

            // console.log( "[RawCompassDataService] Using AbsoluteOrientationSensor" );
        } else if ( typeof DeviceMotionEvent !== "undefined" && typeof DeviceOrientationEvent !== "undefined" ) {
            this.sensorMode = SensorMode.SENSOR_FUSION;

            // console.log( "[RawCompassDataService] Using sensor fusion" );
        } else {
            // Fallback to basic orientation
            this.sensorMode = SensorMode.BASIC;

            // console.log( "[RawCompassDataService] Using basic orientation" );
        }
    }


    /**
     * Set sensor mode manually
     * @param mode - Sensor mode
     */
    public setSensorMode( mode: SensorMode ): void {
        if ( this.active ) {
            console.warn( "[RawCompassDataService] Cannot change mode while active" );

            return;
        }

        this.sensorMode = mode;

        // console.log( `[RawCompassDataService] Sensor mode set to: ${ mode }` );
    }

    /**
     * Get current sensor mode
     * @returns Current sensor mode
     */
    public getSensorMode(): SensorMode {
        return this.sensorMode;
    }

    /**
     * Set calibration offset
     * @param offset - Offset in degrees
     */
    public setCalibrationOffset( offset: number ): void {
        this.calibrationOffset = offset;
        this.calibrated = true;

        // console.log( `[RawCompassDataService] Calibration offset set to: ${ offset.toFixed( 1 ) }°` );

        // Immediately update with new calibration
        if ( this.lastRawHeading !== null ) {
            this.emitCompassData( this.lastRawHeading );
        }
    }

    /**
     * Clear calibration
     */
    public clearCalibration(): void {
        this.calibrationOffset = 0;
        this.calibrated = false;

        // console.log( "[RawCompassDataService] Calibration cleared" );

        // Immediately update without calibration
        if ( this.lastRawHeading !== null ) {
            this.emitCompassData( this.lastRawHeading );
        }
    }

    /**
     * Start compass tracking
     */
    public async start(): Promise<void> {
        if ( this.active ) {
            console.warn( "[RawCompassDataService] Already active" );

            return;
        }

        // Request permission if needed using CompassPermissionService
        const hasPermission = await compassPermissionService.requestPermission();
        if ( !hasPermission ) {
            console.error( "[RawCompassDataService] Permission denied" );

            return;
        }

        this.active = true;

        // Start appropriate sensor mode
        switch ( this.sensorMode ) {
            case SensorMode.ABSOLUTE_ORIENTATION:
                this.startAbsoluteOrientationSensor();
                break;

            case SensorMode.SENSOR_FUSION:
                this.startSensorFusion();
                break;

            case SensorMode.BASIC:
                this.startBasicOrientation();
                break;
        }

        // console.log( "[RawCompassDataService] Started compass tracking" );
    }


    /**
     * Pause Sensor events so that it is not overwritten during a streaming of injected data event
     */
    public pause() {
        // Stop sensors based on mode
        if ( this.absoluteOrientationSensor !== null ) {
            this.absoluteOrientationSensor.stop();
            this.absoluteOrientationSensor = null;
        }

        if ( this.sensorFusion !== null ) {
            // Sensor fusion cleanup handled by removing event listeners
            this.sensorFusion = null;
        }

        // Remove event listeners
        window.removeEventListener( "deviceorientation", this.handleDeviceOrientation );
        window.removeEventListener( "devicemotion", this.handleDeviceMotion );
    }

    /**
     * Restart sensors events
     */
    public resume() {
        switch ( this.sensorMode ) {
            case SensorMode.ABSOLUTE_ORIENTATION:
                this.startAbsoluteOrientationSensor();
                break;

            case SensorMode.SENSOR_FUSION:
                this.startSensorFusion();
                break;

            case SensorMode.BASIC:
                this.startBasicOrientation();
                break;
        }
    }

    /**
     * Stop compass tracking
     */
    public stop(): void {
        if ( !this.active ) {
            return;
        }

        this.active = false;

        // Stop sensors based on mode
        if ( this.absoluteOrientationSensor !== null ) {
            this.absoluteOrientationSensor.stop();
            this.absoluteOrientationSensor = null;
        }

        if ( this.sensorFusion !== null ) {
            // Sensor fusion cleanup handled by removing event listeners
            this.sensorFusion = null;
        }

        // Remove event listeners
        window.removeEventListener( "deviceorientation", this.handleDeviceOrientation );
        window.removeEventListener( "devicemotion", this.handleDeviceMotion );

        //  console.log( "[RawCompassDataService] Stopped compass tracking" );
    }

    /**
     * Start AbsoluteOrientationSensor
     */
    private startAbsoluteOrientationSensor(): void {
        try {
            if ( !window.AbsoluteOrientationSensor ) {
                console.error( "[RawCompassDataService] AbsoluteOrientationSensor not available" );
                this.sensorMode = SensorMode.BASIC;
                this.startBasicOrientation();

                return;
            }

            const sensor = new window.AbsoluteOrientationSensor( { "frequency": 30 } );
            let hasLoggedSensorError = false;

            sensor.addEventListener( "reading", () => {
                if ( sensor.quaternion === null ) {
                    return;
                }

                const heading = this.quaternionToHeading( sensor.quaternion );
                this.lastRawHeading = heading;
                this.emitCompassData( heading );
            } );

            sensor.addEventListener( "error", ( event: Event ) => {
                if ( hasLoggedSensorError ) {
                    return;
                }
                hasLoggedSensorError = true;
                const errorEvent = event as Event & { "error"?: Error };
                console.warn( "[RawCompassDataService] AbsoluteOrientationSensor error (suppressed after first):", errorEvent.error );
            } );

            sensor.start();
            this.absoluteOrientationSensor = sensor;

            // console.log( "[RawCompassDataService] AbsoluteOrientationSensor started" );
        } catch( error ) {
            console.error( "[RawCompassDataService] Failed to start AbsoluteOrientationSensor:", error );

            // Fallback to basic orientation
            this.sensorMode = SensorMode.BASIC;
            this.startBasicOrientation();
        }
    }

    /**
     * Start sensor fusion mode
     */
    private startSensorFusion(): void {
        this.sensorFusion = new SensorFusion( 0, 0.98 );

        // Listen to both orientation and motion events
        window.addEventListener( "deviceorientation", this.handleDeviceOrientation.bind( this ) );
        window.addEventListener( "devicemotion", this.handleDeviceMotion.bind( this ) );

        // console.log( "[RawCompassDataService] Sensor fusion started" );
    }

    /**
     * Start basic orientation mode
     */
    private startBasicOrientation(): void {
        window.addEventListener( "deviceorientation", this.handleDeviceOrientation.bind( this ) );

        // console.log( "[RawCompassDataService] Basic orientation started" );
    }

    /**
     * Handle device orientation event
     * @param event - Device orientation event
     */
    private handleDeviceOrientation = ( event: DeviceOrientationEvent ): void => {
        const now = Date.now();

        // Throttle updates
        if ( now - this.lastUpdateTime < this.UPDATE_INTERVAL ) {
            return;
        }

        this.lastUpdateTime = now;

        let heading: number | null = null;

        // Check for iOS webkit compass heading
        const eventWithWebkit = event as DeviceOrientationEvent & { "webkitCompassHeading"?: number };
        if ( typeof eventWithWebkit.webkitCompassHeading === "number" ) {
            heading = eventWithWebkit.webkitCompassHeading;
        } else if ( event.alpha !== null && event.alpha !== undefined ) {
            // Get screen orientation
            const screenOrientation = window.screen?.orientation?.angle ?? 0;
            heading = this.normalizeAngle( 360 - event.alpha - screenOrientation );
        }

        if ( heading !== null ) {
            this.lastRawHeading = heading;
            this.emitCompassData( heading );
        }
    };

    /**
     * Handle device motion event (for sensor fusion)
     * @param event - Device motion event
     */
    private handleDeviceMotion = ( event: DeviceMotionEvent ): void => {
        if ( this.sensorFusion === null ) {
            return;
        }
        if ( event.accelerationIncludingGravity === null ) {
            return;
        }
        if ( event.rotationRate === null ) {
            return;
        }

        const sensorData: SensorData = {
            "accelerometer": [
                event.accelerationIncludingGravity.x ?? 0,
                event.accelerationIncludingGravity.y ?? 0,
                event.accelerationIncludingGravity.z ?? 0,
            ],
            "gyroscope": [
                event.rotationRate.alpha ?? 0,
                event.rotationRate.beta ?? 0,
                event.rotationRate.gamma ?? 0,
            ],
            "magnetometer": [ 0, 0, 0 ], // Magnetometer data not available from DeviceMotionEvent
            "timestamp":    Date.now(),
        };

        const fusionState = this.sensorFusion.update( sensorData );
        const heading = this.normalizeAngle( fusionState.heading );

        this.lastRawHeading = heading;
        this.emitCompassData( heading );
    };

    /**
     * Convert quaternion to heading
     * @param quaternion - Quaternion [x, y, z, w]
     * @returns Heading in degrees [0, 360)
     */
    private quaternionToHeading( quaternion: number[] ): number {
        const [ x, y, z, w ] = quaternion;

        // Calculate heading from quaternion
        const heading = Math.atan2(
            2 * ( w * z + x * y ),
            1 - 2 * ( y * y + z * z ),
        ) * 180 / Math.PI;

        return this.normalizeAngle( heading );
    }

    /**
     * Normalize angle to [0, 360)
     * @param angle - Angle in degrees
     * @returns Normalized angle
     */
    private normalizeAngle( angle: number ): number {
        return ( ( angle % 360 ) + 360 ) % 360;
    }

    /**
     * Emit compass data to listeners
     * @param rawHeading - Raw heading before calibration
     */
    private emitCompassData( rawHeading: number ): void {
        const calibratedHeading = this.normalizeAngle( rawHeading + this.calibrationOffset );

        const compassData: CompassData = {
            "heading":    calibratedHeading,
            "calibrated": this.calibrated,
            "offset":     this.calibrationOffset,
            "timestamp":  Date.now(),
        };

        // Store last value
        this.lastValue = compassData;

        const event: NavigationEvent<CompassData> = {
            "type":      NavigationEventType.COMPASS_UPDATE,
            "data":      compassData,
            "timestamp": compassData.timestamp,
        };

        this.listeners.forEach( ( listener ) => {
            try {
                listener( event.data );
            } catch( error ) {
                console.error( "[RawCompassDataService] Error in listener:", error );
            }
        } );
    }

    /**
     * Subscribe to compass updates
     * @param listener - Callback function
     * @returns Unsubscribe function
     */
    public subscribe( listener: CompassEventListener ): () => void {
        this.listeners.add( listener );

        // Immediately send last value if available
        if ( this.lastValue !== null ) {
            listener( this.lastValue );
        }

        return () => {
            this.listeners.delete( listener );
        };
    }

    /**
     * Check if service is active
     * @returns True if active
     */
    public isActive(): boolean {
        return this.active;
    }

    /**
     * Get number of active listeners
     * @returns Number of listeners
     */
    public getListenerCount(): number {
        return this.listeners.size;
    }

    /**
     * Inject compass data (for replay mode)
     * @param data - Compass data to inject
     */
    public injectData( data: CompassData ): void {
        // Store last value
        this.lastValue = data;

        // Update calibration state
        this.calibrated = data.calibrated;
        this.calibrationOffset = data.offset;

        // Emit to all listeners
        const event: NavigationEvent<CompassData> = {
            "type":      NavigationEventType.COMPASS_UPDATE,
            "data":      data,
            "timestamp": data.timestamp,
        };

        this.listeners.forEach( ( listener ) => {
            try {
                listener( event.data );
            } catch( error ) {
                console.error( "[RawCompassDataService] Error in listener:", error );
            }
        } );
    }

}

/**
 * Export singleton instance for convenience
 */
export const rawCompassDataService = RawCompassDataService.getInstance();

